import React, { useRef, useState } from "react";
import { useI18n } from "../lib/i18n";
import { uploadImage } from "../lib/api";
import { FONTS, fontStack } from "../lib/fonts";
import { Upload, Trash, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const POSITIONS = [
  "top-left", "top-center", "top-right",
  "center-left", "center", "center-right",
  "bottom-left", "bottom-center", "bottom-right",
];

const WEIGHTS = ["normal", "medium", "semibold", "bold"];
const SIZES = ["sm", "md", "lg", "xl"];
const EFFECTS = ["none", "circle", "lines", "grain"];
const ALIGNS = ["left", "center", "right"];
const IMAGE_POSITIONS = ["background", "top", "bottom"];

const SWATCHES_BG = ["#F9F8F6", "#EAE7DC", "#EBE8E0", "#244A36", "#15251E", "#FFFFFF"];
const SWATCHES_ACCENT = ["#244A36", "#2F5744", "#C48A4E", "#8B3A2E", "#15251E", "#5C6B64"];
const SWATCHES_TEXT = ["#15251E", "#244A36", "#F9F8F6", "#5C6B64"];

function SwatchRow({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((c) => (
        <button
          key={c}
          onClick={() => onChange(c)}
          aria-label={c}
          className={`w-7 h-7 rounded-full border transition ${value === c ? "ring-2 ring-forest ring-offset-2 ring-offset-white" : "border-line hover:scale-110"}`}
          style={{ background: c }}
          data-testid={`${testid}-${c}`}
        />
      ))}
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-7 h-7 rounded-full border border-line cursor-pointer"
        data-testid={`${testid}-picker`}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-2">
      <span className="kicker-label block">{label}</span>
      {children}
    </label>
  );
}

export default function PropertiesPanel({ slide, onChange, onRegenerate, regenerating, onApplyToAll }) {
  const { t, lang } = useI18n();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  if (!slide) return null;

  const update = (k, v) => onChange({ ...slide, [k]: v });

  const doUpload = async (file) => {
    setUploading(true);
    try {
      const res = await uploadImage(file);
      update("image_url", res.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const applyAll = () => {
    if (!onApplyToAll) return;
    if (!window.confirm(lang === "el" ? "Εφαρμογή στιλ σε όλα τα slides;" : "Apply this design to all slides?")) return;
    onApplyToAll();
    toast.success(lang === "el" ? "Εφαρμόστηκε σε όλα" : "Applied to all");
  };

  return (
    <div className="p-6 space-y-6 text-sm" data-testid="properties-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="kicker-label mb-3">{t("editor_props")}</div>
          <div className="font-heading text-ink text-lg">
            Slide {String(slide.index + 1).padStart(2, "0")}
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <button
            onClick={onRegenerate}
            disabled={regenerating}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-forest text-cream hover:bg-forest-dark transition disabled:opacity-50"
            data-testid="regenerate-slide"
            title={lang === "el" ? "Αναγέννηση με AI" : "Regenerate with AI"}
          >
            <ArrowsClockwise size={12} weight="bold" className={regenerating ? "animate-spin" : ""} />
            {regenerating ? "…" : "AI"}
          </button>
          <button
            onClick={applyAll}
            className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1.5 rounded-full border border-forest/30 text-forest hover:bg-forest/5 transition"
            data-testid="apply-to-all"
            title={lang === "el" ? "Εφαρμογή σε όλα" : "Apply to all"}
          >
            <span className="text-[11px]">⊞</span>
            {lang === "el" ? "Σε όλα" : "Apply all"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <Field label="Kicker">
          <input value={slide.kicker || ""} onChange={(e) => update("kicker", e.target.value)} className="cs-input" data-testid="field-kicker" />
        </Field>
        <Field label="Title">
          <textarea value={slide.title || ""} onChange={(e) => update("title", e.target.value)} rows={2} className="cs-input" data-testid="field-title" />
        </Field>
        <Field label="Body">
          <textarea value={slide.body || ""} onChange={(e) => update("body", e.target.value)} rows={3} className="cs-input" data-testid="field-body" />
        </Field>
        <Field label="CTA">
          <input value={slide.cta || ""} onChange={(e) => update("cta", e.target.value)} className="cs-input" data-testid="field-cta" />
        </Field>
      </div>

      <hr className="editorial-rule" />

      {/* Image upload */}
      <div>
        <div className="kicker-label mb-2">Image</div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) doUpload(f);
            e.target.value = "";
          }}
          data-testid="image-file-input"
        />
        {slide.image_url ? (
          <div className="space-y-2">
            <div className="aspect-video w-full rounded-lg border border-line overflow-hidden bg-cream-deep">
              <img src={slide.image_url.startsWith("http") ? slide.image_url : `${process.env.REACT_APP_BACKEND_URL}${slide.image_url}`} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fileRef.current?.click()}
                className="flex-1 text-xs py-2 rounded-full border border-line hover:border-forest hover:text-forest transition"
                disabled={uploading}
                data-testid="image-replace"
              >{uploading ? "…" : "Replace"}</button>
              <button
                onClick={() => update("image_url", "")}
                className="px-3 py-2 rounded-full border border-line hover:border-destructive hover:text-destructive transition"
                data-testid="image-remove"
              ><Trash size={13} /></button>
            </div>
            <Field label="Image placement">
              <div className="flex gap-2" data-testid="image-position-group">
                {IMAGE_POSITIONS.map((p) => (
                  <button key={p} onClick={() => update("image_position", p)} className={`chip ${slide.image_position === p ? "is-active" : ""} px-3 py-1.5 rounded-full text-xs capitalize`} data-testid={`image-pos-${p}`}>{p}</button>
                ))}
              </div>
            </Field>
            {slide.image_position === "background" && (
              <Field label={`Opacity · ${Math.round((slide.image_opacity ?? 0.25) * 100)}%`}>
                <input
                  type="range" min={5} max={100} step={5}
                  value={Math.round((slide.image_opacity ?? 0.25) * 100)}
                  onChange={(e) => update("image_opacity", parseInt(e.target.value) / 100)}
                  className="w-full accent-[#244A36]"
                  data-testid="image-opacity"
                />
              </Field>
            )}
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-full py-4 rounded-lg border border-dashed border-line text-ink-soft hover:text-forest hover:border-forest transition text-xs flex items-center justify-center gap-2"
            data-testid="image-upload-btn"
          >
            <Upload size={14} weight="duotone" />
            {uploading ? "Uploading…" : "Upload image"}
          </button>
        )}
      </div>

      <hr className="editorial-rule" />

      <div>
        <div className="kicker-label mb-2">{t("text_position")}</div>
        <div className="grid grid-cols-3 gap-1.5" data-testid="position-grid">
          {POSITIONS.map((p) => (
            <button key={p} onClick={() => update("text_position", p)} className={`h-10 rounded-md border text-[10px] transition ${
              slide.text_position === p ? "bg-forest text-cream border-forest" : "border-line text-ink-soft hover:border-forest"
            }`} data-testid={`pos-${p}`}>{p.replace("-", "·")}</button>
          ))}
        </div>
      </div>

      <Field label={t("text_align")}>
        <div className="flex gap-2" data-testid="align-group">
          {ALIGNS.map((a) => (
            <button key={a} onClick={() => update("text_align", a)} className={`chip ${slide.text_align === a ? "is-active" : ""} px-3 py-1.5 rounded-full text-xs`} data-testid={`align-${a}`}>{a}</button>
          ))}
        </div>
      </Field>

      <Field label={t("font_weight")}>
        <div className="flex gap-2 flex-wrap" data-testid="weight-group">
          {WEIGHTS.map((w) => (
            <button key={w} onClick={() => update("font_weight", w)} className={`chip ${slide.font_weight === w ? "is-active" : ""} px-3 py-1.5 rounded-full text-xs capitalize`} data-testid={`weight-${w}`}>{w}</button>
          ))}
        </div>
      </Field>

      <Field label={t("font_size")}>
        <div className="flex gap-2" data-testid="size-group">
          {SIZES.map((s) => (
            <button key={s} onClick={() => update("font_size", s)} className={`chip ${slide.font_size === s ? "is-active" : ""} px-3 py-1.5 rounded-full text-xs uppercase`} data-testid={`size-${s}`}>{s}</button>
          ))}
        </div>
      </Field>

      <Field label={t("font_family_label")}>
        <div className="grid grid-cols-2 gap-2" data-testid="font-group">
          {Object.entries(FONTS).map(([k, v]) => (
            <button
              key={k}
              onClick={() => update("font_family", k)}
              className={`text-left px-3 py-2.5 rounded-lg border transition ${
                (slide.font_family || "general_sans") === k
                  ? "border-forest bg-forest/5"
                  : "border-line hover:border-forest/40"
              }`}
              data-testid={`font-${k}`}
              style={{ fontFamily: fontStack(k) }}
            >
              <div className="text-[13px] font-semibold text-ink leading-tight">{v.label}</div>
              <div className="text-[10px] text-ink-soft uppercase tracking-[0.08em] mt-0.5">{v.category}</div>
            </button>
          ))}
        </div>
      </Field>

      <hr className="editorial-rule" />

      <Field label={t("bg")}>
        <SwatchRow value={slide.bg_color} onChange={(v) => update("bg_color", v)} options={SWATCHES_BG} testid="bg-swatch" />
      </Field>
      <Field label={t("accent")}>
        <SwatchRow value={slide.accent_color} onChange={(v) => update("accent_color", v)} options={SWATCHES_ACCENT} testid="accent-swatch" />
      </Field>
      <Field label={t("text_color")}>
        <SwatchRow value={slide.text_color} onChange={(v) => update("text_color", v)} options={SWATCHES_TEXT} testid="text-swatch" />
      </Field>

      <Field label={t("effect")}>
        <div className="flex gap-2 flex-wrap" data-testid="effect-group">
          {EFFECTS.map((e) => (
            <button key={e} onClick={() => update("effect", e)} className={`chip ${slide.effect === e ? "is-active" : ""} px-3 py-1.5 rounded-full text-xs capitalize`} data-testid={`effect-${e}`}>{e}</button>
          ))}
        </div>
      </Field>
    </div>
  );
}
